import { Flex, Stack, Text } from '@chakra-ui/react'
import { useMemo } from 'react'

import {
  isSummerCampInSession,
  SUMMER_CAMP_COLORS,
  SUMMER_CAMP_END_DATE,
  SUMMER_CAMP_ICON,
  SUMMER_CAMP_START_DATE,
  SUMMER_CAMP_YEAR,
} from '@/client/services/summerCamp'
import { getSummerCampUrl } from '@/client/services/url'

import type { NavbarBannerType } from '@/components/Layout/Navbar/types'
import ShwLink from '@/components/ShwLink'
import Icon from '@/components/UI/Icon/Icon'

/**
 * This banner is being kept as-is instead of being converted into a data loader banner
 * Because it uses a purple background color, and therefore all the text needs to be modified.
 * I (Anees) am not aware of a way to modify things for this kind-of usecase in richtext.
 * Maybe we can override the context but that would break more things than it'll fix.
 */
export default function useSummerCampBanner(): NavbarBannerType | null {
  const summerCampUrl = getSummerCampUrl()

  const banner: NavbarBannerType = useMemo(
    () => ({
      name: 'summer-camp',
      slug: `summer-camp-${SUMMER_CAMP_YEAR}`,
      sticky: false,
      link: summerCampUrl,
      hideMegaphone: true,
      children: (
        <Stack direction="row" alignItems="center">
          <Flex display={['none', 'flex']}>
            <Icon faIcon={SUMMER_CAMP_ICON} color={SUMMER_CAMP_COLORS.lightColor} />
          </Flex>
          <Text fontWeight="bold" color="purple.contrast" display={['none', 'none', 'block']}>
            Learn something new this summer at Schoolhouse Summer Camp {SUMMER_CAMP_YEAR}! In
            session {SUMMER_CAMP_START_DATE.toFormat('MMMM d')} to{' '}
            {SUMMER_CAMP_END_DATE.toFormat('MMMM d')}.{' '}
            <ShwLink
              href={getSummerCampUrl()}
              color="purple.contrast"
              _hover={{ color: SUMMER_CAMP_COLORS.lightColor }}
            >
              Learn More
            </ShwLink>
          </Text>
          {/* Show version with shortened text on small screens */}
          <Text fontWeight="bold" color="purple.contrast" display={['block', 'block', 'none']}>
            Learn something new this summer at Schoolhouse{' '}
            <ShwLink
              href={getSummerCampUrl()}
              color="purple.contrast"
              _hover={{ color: 'cyan.subtle' }}
            >
              Summer Camp {SUMMER_CAMP_YEAR}
            </ShwLink>
            !
          </Text>
        </Stack>
      ),
      colorScheme: 'summerCamp',
    }),
    [summerCampUrl]
  )

  if (isSummerCampInSession()) {
    return banner
  }

  return null
}
